import { EFFORT_LEVELS, EffortStop } from '@parsleyai/ui'
import { ChefHatIcon, CircleDashedIcon, CookingPotIcon, type Icon, LightningIcon } from '@phosphor-icons/react'
import { DayOfWeek, type EffortLevel, EffortMode, type EffortPreferences, type EffortRule } from '@/types/eatingPreferences'

export const DAYS: readonly DayOfWeek[] = [
  DayOfWeek.Mon,
  DayOfWeek.Tue,
  DayOfWeek.Wed,
  DayOfWeek.Thu,
  DayOfWeek.Fri,
  DayOfWeek.Sat,
  DayOfWeek.Sun
]

export enum DayGroup {
  Weekdays = 'weekdays',
  Weekend = 'weekend'
}

export const DAY_GROUPS: Record<DayGroup, readonly DayOfWeek[]> = {
  [DayGroup.Weekdays]: [DayOfWeek.Mon, DayOfWeek.Tue, DayOfWeek.Wed, DayOfWeek.Thu, DayOfWeek.Fri],
  [DayGroup.Weekend]: [DayOfWeek.Sat, DayOfWeek.Sun]
}

export const LEVELS = EFFORT_LEVELS as readonly EffortLevel[]

/** Nights in a week — the cap on counts, and what by-day resolves over. */
export const WEEK = 7

export const STOP_ICONS: Record<EffortStop, Icon> = {
  [EffortStop.Any]: CircleDashedIcon,
  [EffortStop.Quick]: LightningIcon,
  [EffortStop.Moderate]: CookingPotIcon,
  [EffortStop.Involved]: ChefHatIcon
}

export const EFFORT_DEFAULT: EffortPreferences = {
  mode: EffortMode.ByDay,
  days: { base: EffortStop.Any, rules: [] },
  counts: { [EffortStop.Quick]: 0, [EffortStop.Moderate]: 0, [EffortStop.Involved]: 0 }
}

const isIn = <T extends string>(values: readonly T[], value: unknown): value is T => values.includes(value as T)
const isCount = (n: unknown): n is number => Number.isInteger(n) && (n as number) >= 0 && (n as number) <= WEEK

function readRule(raw: unknown): EffortRule | null {
  const rule = raw as Partial<EffortRule> | null
  if (typeof rule?.id !== 'string' || !isIn(LEVELS, rule.level) || !Array.isArray(rule.days)) return null
  return { id: rule.id, level: rule.level, days: rule.days.filter((d) => isIn(DAYS, d)) }
}

/** A stored effort preference, or null for anything else — including the old weekday/weekend shape. */
export function readEffort(raw: unknown): EffortPreferences | null {
  const effort = raw as Partial<EffortPreferences> | null
  if (!effort || !isIn(Object.values(EffortMode), effort.mode)) return null
  const base = effort.days?.base
  const counts = effort.counts as Partial<Record<EffortLevel, unknown>> | undefined
  if (!isIn(Object.values(EffortStop), base) || !Array.isArray(effort.days?.rules)) return null
  if (!counts || !LEVELS.every((level) => isCount(counts[level]))) return null
  return {
    mode: effort.mode,
    days: { base, rules: effort.days.rules.map(readRule).filter((r): r is EffortRule => r !== null) },
    counts: Object.fromEntries(LEVELS.map((level) => [level, counts[level]])) as Record<EffortLevel, number>
  }
}

/** The level each day works out to: a rule's level, else the usual one. */
export function resolveDays(days: EffortPreferences['days']): Record<DayOfWeek, EffortStop> {
  const map = Object.fromEntries(DAYS.map((d) => [d, days.base])) as Record<DayOfWeek, EffortStop>
  for (const rule of days.rules) if (rule.level) for (const d of rule.days) map[d] = rule.level
  return map
}

export interface LevelTally {
  level: EffortLevel
  n: number
}

/** Non-zero levels, most first; ties keep quick → involved order. */
function sortTally(entries: LevelTally[]): LevelTally[] {
  return entries.filter(({ n }) => n > 0).sort((a, b) => b.n - a.n)
}

export function tallyDays(days: EffortPreferences['days'], except?: EffortStop): LevelTally[] {
  const map = resolveDays(days)
  return sortTally(LEVELS.filter((level) => level !== except).map((level) => ({ level, n: DAYS.filter((d) => map[d] === level).length })))
}

export function tallyCounts(counts: EffortPreferences['counts']): LevelTally[] {
  return sortTally(LEVELS.map((level) => ({ level, n: counts[level] })))
}

/** Days left on Any. */
export function openDays(days: EffortPreferences['days']): number {
  const map = resolveDays(days)
  return DAYS.filter((d) => map[d] === EffortStop.Any).length
}

export const placedCount = (counts: EffortPreferences['counts']) => LEVELS.reduce((sum, level) => sum + counts[level], 0)

export const claimedDays = (rules: EffortRule[]) => rules.reduce((sum, rule) => sum + rule.days.length, 0)

/** The usual level while it covers most of the week; otherwise Any, so the week reads as plain counts. */
export const prevailingBase = (days: EffortPreferences['days']): EffortStop =>
  (WEEK - claimedDays(days.rules.filter((r) => r.level))) * 2 > WEEK ? days.base : EffortStop.Any

/** Toggles days on a rule. A day joining one rule leaves any other; a group goes on or off as one. */
export function toggleRuleDays(rules: EffortRule[], id: string, days: readonly DayOfWeek[]): EffortRule[] {
  const rule = rules.find((r) => r.id === id)
  if (!rule) return rules
  const allOn = days.every((d) => rule.days.includes(d))
  return rules.map((r) => {
    if (r.id === id)
      return { ...r, days: allOn ? r.days.filter((d) => !days.includes(d)) : DAYS.filter((d) => r.days.includes(d) || days.includes(d)) }
    return allOn ? r : { ...r, days: r.days.filter((d) => !days.includes(d)) }
  })
}

/** The group a rule's days exactly match, if any. */
export function matchGroup(days: readonly DayOfWeek[]): DayGroup | null {
  const match = Object.entries(DAY_GROUPS).find(([, group]) => group.length === days.length && group.every((d) => days.includes(d)))
  return match ? (match[0] as DayGroup) : null
}

/** True when the way in effect says nothing — the same test every effort summary uses for "not set". */
export function effortSaysNothing(prefs: EffortPreferences | null): boolean {
  if (!prefs || prefs.mode === EffortMode.ByDay) {
    const days = prefs?.days ?? EFFORT_DEFAULT.days
    const base = prevailingBase(days)
    return base === EffortStop.Any && tallyDays(days, base).length === 0
  }
  return tallyCounts(prefs.counts).length === 0
}

/** What gets saved: rules without days or a level dropped, and null when neither way says anything. */
export function toStoredEffort(prefs: EffortPreferences): EffortPreferences | null {
  const rules = prefs.days.rules.filter((r) => r.days.length > 0 && r.level)
  const days = { base: prefs.days.base, rules }
  const empty = days.base === EffortStop.Any && rules.length === 0 && placedCount(prefs.counts) === 0
  return empty ? null : { ...prefs, days }
}
